import { canonicalRegion, DEFAULT_REGION } from '../config/regions';

/**
 * How this app writes a number, in one place.
 *
 * Two things decide it, and they belong to different people. **The region is
 * the organization's** — the decimal mark, the thousands separator and where
 * the currency symbol sits are conventions of the country it bills from.
 * **The language is the reader's**, the same one that already gives them their
 * dates. They join here, and nowhere else.
 *
 * Three shapes of amount, three functions, because confusing them is what made
 * a tariff of $0.0079 read as $0.01 on one column and 0.007900 CAD on the one
 * beside it:
 *
 * - {@link formatCurrency} — what is charged, owed or held. Two decimals.
 * - {@link formatTariff} — a unit price or a provider's cost. Up to six, and
 *   no trailing zeros: a tariff rounded to the cent stops being a tariff.
 * - {@link formatDecimal} — a number with no currency at all.
 */

const FALLBACK = { region: DEFAULT_REGION, currency: 'CAD', country: 'CA' };

/**
 * Where the active organization comes from, handed in rather than reached for.
 *
 * This module must stay importable on its own: the stores import through the
 * bundler's `@/` alias, so a formatter that imported them could not be loaded
 * by anything without a bundler — which is how the previous version ended up
 * calling `require()` inside a `catch`, throwing on every call in the browser
 * and reading the organization nowhere. The app injects the lookup at boot
 * instead, and everything else keeps working without it.
 *
 * @type {null | (() => { region?: string, currency?: string, country?: string } | null)}
 */
let resolver = null;

export function provideMoneyContext(fn) {
    resolver = fn;
}

/**
 * Whose conventions apply here.
 *
 * The injected lookup answers for the portal and the app. When it has nothing
 * to say — the platform console, which belongs to no organization — the answer
 * is Elixio's own back office, which is what the console is a console for.
 *
 * Guarded because a formatter may run before Pinia exists, during the first
 * paint or in a spec: a screen that throws while printing a price is worse
 * than one that prints it the house way.
 */
function context() {
    let given = null;

    try {
        given = resolver ? resolver() : null;
    } catch {
        given = null;
    }

    if (given?.currency) {
        return {
            region: canonicalRegion(given.region),
            currency: given.currency,
            country: (given.country || FALLBACK.country).toUpperCase()
        };
    }

    const main = window.config?.mainOrganization;

    if (main?.currency_code) {
        return { region: canonicalRegion(main.region), currency: main.currency_code, country: FALLBACK.country };
    }

    return FALLBACK;
}

/** The organization's own country, for what is formatted by country and not by region. */
export function activeCountry() {
    return context().country;
}

/** The reader's language, which is the one their dates already speak. */
function language() {
    const locale = window.i18n?.locale;
    const value = typeof locale === 'string' ? locale : locale?.value;

    return (value || window.config?.locale || 'en').split('-')[0];
}

export function activeLocale() {
    return `${language()}-${context().region}`;
}

export function activeCurrency() {
    return context().currency;
}

/**
 * What is charged, owed or held. Two decimals, always — a balance with six
 * would read as a measurement rather than as money.
 */
export function formatCurrency(value, currencyOverride) {
    return format(value, currencyOverride, { minimumFractionDigits: 2 });
}

/**
 * A unit price, a markup, a bound, a provider's cost.
 *
 * Two decimals at least, six at most, and no trailing zeros: `0.02` stays
 * `0,02 $` while `0.0079` keeps every figure it needs. Rounding this one to
 * the cent turns two different tariffs into the same number, which is the one
 * thing a price list exists to prevent.
 */
export function formatTariff(value, currencyOverride) {
    return format(value, currencyOverride, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

export function formatDecimal(value, options = {}) {
    return new Intl.NumberFormat(activeLocale(), {
        style: 'decimal',
        minimumFractionDigits: 2,
        ...options
    }).format(Number(value) || 0);
}

/**
 * The ISO code, and only when it would otherwise be ambiguous.
 *
 * The house rule, already followed by every invoice and product screen: a
 * symbol on its own for the organization's own currency, the code beside it
 * when the amount is in another one. Shown apart from the figure, never
 * glued to it.
 */
export function foreignCurrencyCode(currency) {
    if (!currency) return '';

    return currency.toUpperCase() === activeCurrency().toUpperCase() ? '' : currency.toUpperCase();
}

function format(value, currencyOverride, options) {
    const currency = currencyOverride || activeCurrency();

    try {
        return new Intl.NumberFormat(activeLocale(), { style: 'currency', currency, ...options }).format(
            Number(value) || 0
        );
    } catch {
        // An unknown currency code reaches Intl as a RangeError. Falling back
        // keeps the figure readable instead of blanking the screen.
        return new Intl.NumberFormat(activeLocale(), { style: 'decimal', ...options }).format(Number(value) || 0);
    }
}
